import { IANA_ZONE_NAMES } from './iana-zone-names.ts'

/**
 * IANA zone names keyed by their lower-cased spelling.
 *
 * Built once, at first use, from a constant list, and never written to afterwards: a lookup
 * table, not state. `IANA_ZONE_NAMES` has no two names that differ only in case (its test says
 * so), so the key is unambiguous.
 */
let byLowerCase: ReadonlyMap<string, string> | undefined

function lookup(): ReadonlyMap<string, string> {
  byLowerCase ??= new Map(IANA_ZONE_NAMES.map((name) => [name.toLowerCase(), name]))
  return byLowerCase
}

/**
 * A zone name in IANA's own letter case — `europe/berlin` becomes `Europe/Berlin`, `utc` becomes
 * `UTC` — or the value untouched when it is not a name on the list.
 *
 * **Case, and only case.** An alias is never swapped for the name that replaced it
 * (`europe/kiev` is `Europe/Kiev`, not `Europe/Kyiv`; `asia/calcutta` is `Asia/Calcutta`): the
 * string a person chose is theirs, and two spellings of one zone are not an error. Nothing is
 * trimmed either — ` Europe/Berlin` is not a zone today and stays one that is refused.
 *
 * **Deterministic across runtimes.** It matches against a static list and asks `Intl` nothing,
 * which is the point: `Intl.DateTimeFormat().resolvedOptions().timeZone` is ICU's opinion, ICU
 * differs between workerd, Node and each browser, and it may rewrite an alias. Whether the
 * returned string is a zone this runtime *accepts* is a different question, answered by
 * `isValidTimeZone`; a caller that must refuse a non-zone runs that after this, on the result.
 * A value that is a zone for ICU but newer than the list is returned as it came.
 *
 * Idempotent: `canonicalTimeZoneCase(canonicalTimeZoneCase(x))` is `canonicalTimeZoneCase(x)`.
 */
export function canonicalTimeZoneCase(value: string): string {
  return lookup().get(value.toLowerCase()) ?? value
}
